import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { selectButtonActiveStyle } from '@databases/context-menu/styles';
import {
  BORDER_BLUE,
  BORDER_GREEN,
  BORDER_ORANGE,
  BORDER_PURPLE,
} from '@databases/database-ai-first-creation-ui';
import {
  FieldNameInlineContainer,
  FieldNameWithActionsContainer,
} from '@databases/field-name/FieldNameWithActions';
import {
  BorderContainer,
  InlineEditWrapperContainer,
} from '@databases/inline-edit/InlineEditWrapper';
import Z_INDEX from '@databases/layout/z-index';
import {
  TABLE_BORDER_RADIUS,
  VISUAL_REFRESH_TABLE_BORDER_RADIUS,
} from '@databases/styles';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import styled, { css } from 'styled-components';

import {
  newTableBorderWidth,
  subtleBorder,
  SubtleTable,
  tableRowMinHeight,
} from '../../styled';

import { AddFieldButtonContainer } from './AddFieldButton';
import { StyledDot } from './AddFieldDot';

const INSERT_INDICATOR_HEIGHT_OFFSET = 10;

type InsertColumnIndicatorProps = {
  containerHeight: number;
};

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const InsertColumnIndicator = css<InsertColumnIndicatorProps>`
  &.${TABLE_CLASSNAMES.INSERT_COLUMN_BEFORE}, &.${TABLE_CLASSNAMES.INSERT_COLUMN_AFTER} {
    z-index: 1;

    &:before {
      content: ' ';
      position: absolute;
      top: 0;
      height: ${({ containerHeight }) =>
        `${containerHeight - INSERT_INDICATOR_HEIGHT_OFFSET}px`};
      width: 2px;
      background-color: ${colors.B200};
      pointer-events: none;
    }

    &.${TABLE_CLASSNAMES.INSERT_COLUMN_BEFORE} {
      &:before {
        left: -1px;
      }
    }

    &.${TABLE_CLASSNAMES.INSERT_COLUMN_AFTER} {
      &:before {
        right: -1px;
      }
    }
  }
`;

interface TableProps extends InsertColumnIndicatorProps {
  minWidth: number;
  gridTemplateColumns: string;
  actionsMidWidth: number;
  canEditContent?: boolean;
  hasNumberedEntries?: boolean;
  isVisualRefreshM1Enabled: boolean;
}

export const TableLayoutWrapper = styled.div<{ containerHeight?: number }>``;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
export const Table = styled(SubtleTable)<TableProps>`
  overflow-wrap: anywhere;
  width: 100%;
  box-sizing: border-box;
  min-width: ${(props) => `${props.minWidth}px`};
  background: ${token('elevation.surface', '#FFFFFF')};

  .${TABLE_CLASSNAMES.SELECT_BUTTON} {
    display: none;
    transition: opacity 0.2s;
    position: relative;
    z-index: ${Z_INDEX.COLUMN_SELECT_BUTTON};
    ${(props) =>
      !props.isVisualRefreshM1Enabled &&
      `background-color: ${token('elevation.surface.sunken', '#F7F8F9')}`};
    color: ${colors.N200};
    border-radius: ${token('radius.medium', '6px')};

    &:hover {
      ${(props) => !props.isVisualRefreshM1Enabled && selectButtonActiveStyle};
      cursor: pointer;
    }

    .${TABLE_CLASSNAMES.DRAG_HANDLER_ICON} {
      display: flex;
    }

    .${TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON} {
      display: none;
    }

    &.${TABLE_CLASSNAMES.SHOW_AS_DRAGGING} {
      display: flex;
      ${selectButtonActiveStyle};
      cursor: grabbing;

      .${TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON} {
        display: flex;
      }

      .${TABLE_CLASSNAMES.DRAG_HANDLER_ICON} {
        display: none;
      }
    }
  }

  &,
  > * {
    display: flex;
    flex-direction: column;
  }

  > * > tr {
    display: grid;
    grid-template-columns: ${(props) => props.gridTemplateColumns};
  }

  > * > tr > * {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0;
    box-sizing: border-box;
    min-width: 0;
    > ${FieldNameWithActionsContainer}, > ${InlineEditWrapperContainer} {
      flex-grow: 1;
    }
  }

  > * > tr > .entry-number-table-header,
  > * > tr > .entry-number-table-data {
    position: sticky;
    ${(props) =>
      !props.canEditContent &&
      `
    left: 0;
  `}
  }

  > thead > tr > th {
    ${InsertColumnIndicator};
  }

  > thead > tr > th:nth-last-child(2) {
    ${FieldNameWithActionsContainer} {
      margin-right: 0;
    }

    border-right: ${subtleBorder};
    border-width: ${newTableBorderWidth}px;
    border-start-end-radius: ${({ isVisualRefreshM1Enabled }) =>
      isVisualRefreshM1Enabled
        ? VISUAL_REFRESH_TABLE_BORDER_RADIUS
        : TABLE_BORDER_RADIUS};
    border-end-end-radius: ${({ isVisualRefreshM1Enabled }) =>
      isVisualRefreshM1Enabled
        ? VISUAL_REFRESH_TABLE_BORDER_RADIUS
        : TABLE_BORDER_RADIUS};
    border-top-right-radius: ${({ isVisualRefreshM1Enabled }) =>
      isVisualRefreshM1Enabled && VISUAL_REFRESH_TABLE_BORDER_RADIUS};
    border-bottom-end-radius: ${({ isVisualRefreshM1Enabled }) =>
      isVisualRefreshM1Enabled && VISUAL_REFRESH_TABLE_BORDER_RADIUS};
  }

  // Visual refresh styling changes for read only table
  ${(props) =>
    props.isVisualRefreshM1Enabled &&
    props.hasNumberedEntries &&
    !props.canEditContent &&
    `
    tbody > tr > td:first-child {
    border-left: ${token('border.width', '1px')} solid transparent;
    border-bottom: 0px solid transparent;
    background-image: linear-gradient(to left, ${token('color.border')}, transparent);
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: 100% 1px;

    &.${TABLE_CLASSNAMES.IS_SELECTED} {
    border-bottom: 0px solid transparent;
    background-image: linear-gradient(to left, ${token('color.border')}, transparent);
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: 100% 1px;
    }

    &.${TABLE_CLASSNAMES.IS_SELECTED} > div:first-child {
      margin-bottom: 0;
    }
    }

    tbody > tr:last-child > td:first-child {
    &.${TABLE_CLASSNAMES.IS_SELECTED} {
          border-bottom: ${token('border.width', '1px')} solid transparent;
    }
    }

    tbody > tr > td:nth-last-child(2) {
    border-right: none;
    border-bottom: 0px solid transparent;
    background-image: linear-gradient(to right, ${token('color.border')} 0%, ${token(
      'color.border'
    )} 90%,transparent 100%);
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: 100% 1px;

    &.${TABLE_CLASSNAMES.IS_SELECTED} {
      border-bottom: ${token('border.width', '1px')} solid ${token('color.border')};
    }
    }
    `}

  // If numbered entries is enabled, the 3rd child refers to the first column in the database.
  // Since numbered entries has a right border, we do not need a left border on the first column.
  > thead > tr > :nth-child(3) {
    ${(props) =>
      props.hasNumberedEntries &&
      props.canEditContent &&
      `
      border-left: none;
    `}
  }

  // If numbered entries is enabled, and user cannot edit content, the 2nd child refers to the first column in the
  // database. Since numbered entries has a right border, we do not need a left border on the first column.
  > thead > tr > :nth-child(2) {
    ${(props) =>
      props.hasNumberedEntries &&
      !props.canEditContent &&
      `
      border-left: none;
    `}
  }

  > thead > tr > th:nth-child(${({ canEditContent }) => (canEditContent ? 2 : 1)}) {
    ${(props) =>
      props.isVisualRefreshM1Enabled
        ? `border-start-start-radius: ${VISUAL_REFRESH_TABLE_BORDER_RADIUS};
      border-end-start-radius: ${VISUAL_REFRESH_TABLE_BORDER_RADIUS};
      `
        : `border-start-start-radius: ${TABLE_BORDER_RADIUS}`}
  }

  > thead > tr > th.${TABLE_CLASSNAMES.ADD_FIELD} {
    z-index: 1;
    right: ${token('space.negative.025', '-2px')};

    ${AddFieldButtonContainer} {
      position: relative;
      left: -39px;
      height: calc(100% - 1px);
      width: 40px;
      border-width: ${newTableBorderWidth}px;
      ${(props) =>
        !props.isVisualRefreshM1Enabled &&
        `border: ${token('border.width', '1px')} solid transparent;
      border-left: ${token('border.width', '1px')} solid ${token(
          'color.border',
          '#091E4224'
        )};
      box-sizing: border-box;`}
      z-index: 1;
    }
  }

  > thead > tr > th:not(.${TABLE_CLASSNAMES.ADD_FIELD}) {
    ${FieldNameInlineContainer} {
      padding: ${token('space.050', '4px')};
    }

    svg {
      height: 18px;
      width: auto;
    }
  }

  > thead > tr > th,
  > tfoot > tr > td,
  > tbody > tr > td {
    min-height: ${tableRowMinHeight}px;
  }

  // For visual refresh, remove the table's vertical right and left borders
  // Apply subtle gradient fade effect to horizontal borders at row boundaries
  ${(props) =>
    props.isVisualRefreshM1Enabled &&
    props.canEditContent &&
    `
    // First column border bottom gradient
    > tbody > tr > td:nth-child(2) {
    border-left: ${token('border.width', '1px')} solid transparent;
    border-bottom: 0px solid transparent;
    background-image: linear-gradient(to left, ${token('color.border')}, transparent);
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: 100% 1px;

    &.${TABLE_CLASSNAMES.IS_SELECTED} {
    border-bottom: 0px solid transparent;
    background-image: linear-gradient(to left, ${token('color.border')}, transparent);
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: 100% 1px;
    }

    &.${TABLE_CLASSNAMES.IS_SELECTED} > div:first-child {
      margin-bottom: 0;
    }
    }

    // Targets the last column and last cell of the database
    > tbody > tr:last-child > td:nth-last-child(2) {
    &.${TABLE_CLASSNAMES.IS_SELECTED} {
      margin-bottom: -1px;
    }
    }

    // Last column border bottom gradient
    > tbody > tr > td:nth-last-child(2) {
    border-right: ${token('border.width', '1px')} solid transparent;
    border-bottom: 0px solid transparent;
    background-image: linear-gradient(to right, ${token('color.border')} 0%, ${token(
      'color.border'
    )} 90%,transparent 100%);
    background-repeat: no-repeat;
    background-position: bottom;
    background-size: 100% 1px;

    &.${TABLE_CLASSNAMES.IS_SELECTED} {
      border-bottom: ${token('border.width', '1px')} solid ${token('color.border')};
    }
    }

    // Remove the last row only to get rid of double border due to Add entry button
    > tbody > tr:last-child > td:nth-child(2),
    > tbody > tr:last-child > td:nth-last-child(2) {
     background-size: 100% 0px;
    }
    `}

  > tbody > tr > *:nth-last-child(2) {
    border-right: ${subtleBorder};
    border-width: ${newTableBorderWidth}px;
  }

  > tbody > tr > :nth-child(3) {
    ${(props) =>
      props.hasNumberedEntries &&
      props.canEditContent &&
      `
      border-left: none;
    `}
  }

  > tbody > tr > :nth-child(2) {
    ${(props) =>
      props.hasNumberedEntries &&
      !props.canEditContent &&
      `
      border-left: none;
    `}
  }

  > tbody > tr:first-of-type > * {
    ${BorderContainer} {
      top: 1px;
    }
  }

  > tbody > tr:last-of-type > * {
    ${BorderContainer} {
      bottom: 1px;
    }
  }

  /* Animated gradient for cell selection during Rovo streaming */
  &::before {
    content: '';
    position: absolute;
    visibility: var(--rovo-visibility, hidden);
    top: var(--rovo-cell-top, 0px);
    left: var(--rovo-cell-left, 0px);
    width: var(--rovo-cell-width, 0px);
    height: calc(var(--rovo-cell-height, 0px) - 2px);
    border-radius: ${token('radius.small', '3px')};
    padding: ${token('space.025', '2px')};
    background: conic-gradient(
      from var(--angle, 0deg),
      ${BORDER_ORANGE} 25%,
      ${BORDER_GREEN} 25% 50%,
      ${BORDER_BLUE} 50% 75%,
      ${BORDER_PURPLE} 75% 100%
    );
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 300;
  }

  td {
    &.${TABLE_CLASSNAMES.IS_SELECTED}:not(:focus-within) {
      background: ${token('color.background.accent.blue.subtlest', '#E9F2FF')};
    }

    &.${TABLE_CLASSNAMES.IS_SOFT_SELECTED}:not(:focus-within) {
      background-color: transparent !important;
    }

    &.${TABLE_CLASSNAMES.SHOW_AS_DISABLED}:not(:focus-within) {
      opacity: 0.4;
    }

    &.${TABLE_CLASSNAMES.IS_ROVO_EDIT_PROCESSING} {
      opacity: 0.4;
    }

    &.${TABLE_CLASSNAMES.IS_ROVO_EDIT_PROCESSING} {
      background: ${token('color.background.accent.blue.subtlest')} !important;
      opacity: 0.4 !important;
    }
  }

  th {
    &.${TABLE_CLASSNAMES.IS_ROVO_EDIT_PROCESSING} {
      background: ${token('elevation.surface.overlay.hovered')} !important;
    }
  }

  th.${TABLE_CLASSNAMES.ADD_FIELD}, td.${TABLE_CLASSNAMES.ACTIONS} {
    position: sticky;
    right: 0;
    background: ${token('elevation.surface', '#FFFFFF')};
    border-left: ${subtleBorder};
    border-bottom: ${subtleBorder};
    border-width: ${newTableBorderWidth}px;
    min-width: ${(props) => `${props.actionsMidWidth}px`};
    justify-content: center;

    > * {
      flex-grow: 1;
    }
  }

  // Show insert dots of the next selected siblings when hovering a selected cell (only on column selection).

  > * > tr:not(.${TABLE_CLASSNAMES.IS_SELECTED}) > *.${TABLE_CLASSNAMES.IS_SELECTED}:is(:hover) ~ *.${TABLE_CLASSNAMES.IS_SELECTED} ${StyledDot},

    // Show insert dots of the previous selected siblings when hovering a selected cell (only on column selection).
  > * > tr:not(.${TABLE_CLASSNAMES.IS_SELECTED}) > *.${TABLE_CLASSNAMES.IS_SELECTED}:has(~ .${TABLE_CLASSNAMES.IS_SELECTED}:is(:hover)) ${StyledDot},

    // Show insert dots when hovering a single cell.
  > * > tr > td:hover ${StyledDot} {
    opacity: 1;
    z-index: ${Z_INDEX.TABLE_VIEW_INSERT_DOT};
  }
`;
